import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@bonanza-social/i18n';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { Box, Button, Image, Loader, Text } from '@bonanza-social/moonlight';
import { launchGame, selectGameIdByName } from '../../state';
import { METRICS } from './GameInfo.content';
import { GameInfoProps } from './GameInfo.types';
import useGameInfo from '../../hooks/useGameInfo';

function GameInfo({ name, ...rest }: GameInfoProps) {
  const dispatch = useDispatch();
  const id = useSelector((state) => selectGameIdByName(state, name));
  const [t] = useTranslation('games');
  const { data, dataState } = useGameInfo(id);
  const image = useMemo(
    () => data?.images.find((image) => image.name === 'landscape'),
    [data],
  );

  const onPlayNow = useCallback(() => {
    if (id) {
      dispatch(launchGame({ id, currencyCode: 'BTY' }));
    }
  }, [id, dispatch]);

  if (dataState !== 'fulfilled') {
    return <Loader margin="auto" />;
  }

  return (
    <Box
      padding={{
        mobile: 'space-800',
        tablet: 'space-1000',
        desktop: 'space-1000',
      }}
      {...rest}
    >
      <Image
        source={image?.url}
        aspectRatio={1.775}
        width="100%"
        maxWidth={750}
        marginHorizontal="auto"
        marginBottom={{
          mobile: 'space-800',
          tablet: 'space-1000',
          desktop: 'space-1000',
        }}
        borderRadius="radius-300"
      />
      <Text variant="h4" gradient="darkSurfaceAccent">
        {data?.name}
      </Text>
      <Box flexDirection="row" paddingVertical="space-400">
        {METRICS.filter((metric) => !!data?.[metric]).map((metric) => (
          <Box key={metric} gap="space-200" width="20%" minWidth={100}>
            <Text
              variant="caption-semi"
              locNS="games"
              locKey={metric}
              color="darkSurfaceTertiary"
            />
            <Text variant="h5" color="darkSurfacePrimary">
              {data?.[metric]}
            </Text>
          </Box>
        ))}
      </Box>
      <Button
        locNS="games"
        locKey="playNow"
        marginBottom={{
          mobile: 'space-600',
          tablet: 'space-1000',
          desktop: 'space-1000',
        }}
        onPress={onPlayNow}
      />
      <Text
        variant="body-l"
        gradient="darkSurfaceAccent"
        paddingBottom="space-200"
      >
        {t('aboutGame', { game: data?.name })}
      </Text>
      <Text variant="body-m" color="darkSurfacePrimary">
        {data?.description}
      </Text>
    </Box>
  );
}

export default memo(GameInfo);
